import { SubmissionStateService } from '../../../services/submission-state.service';
import { ProcessFormStateService } from '../../../services/process-form-state.service';
import { TreeNode } from 'primeng/api';
import { IFileInfo, RawFileInfo } from '../../models/file-info';

import { Directive, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { MainFacade } from '../../main.facade';
import { Request } from '../../models/request';
import { map, take } from 'rxjs/operators';
import { DialogService } from 'primeng/dynamicdialog';
import {
	DirectorySubmitDialogComponent,
	DirectorySubmitDialogResult,
} from './directory-submit-dialog/directory-submit-dialog.component';
import { CreateRuleDialogComponent } from './create-rule-dialog/create-rule-dialog.component';

 @Directive()
 export abstract class ProcessComponentBase {

	form!: FormGroup;
	public bucket: string | undefined = undefined;
	public path: string | undefined = undefined;
	public selectedNode: TreeNode<IFileInfo> | null = null;
	validationMessage: string | null = null;

	// Snapshot store survives this component's destroy/rebuild cycle when the
	// user toggles right-panel tabs or switches process via the dropdown.
	// Resolved in the constructor body (not a field initializer) so unit
	// tests that hand-construct a component with `new ...()` outside an
	// Angular DI context get a fresh standalone instance instead of an
	// NG0203 from `inject()`.
	protected formStateService: ProcessFormStateService;

	// Same NG0203-tolerant pattern as formStateService - undefined outside a
	// real Angular DI context (e.g. `new SomeComponent(...)` in a unit test),
	// in which case the folder-submit dialog is simply skipped (see submit()).
	private dialogService: DialogService | undefined;

	// Same NG0203-tolerant pattern as formStateService - undefined outside a
	// real Angular DI context
	protected destroyRef: DestroyRef | undefined;

	// Whether "Create Rule" can be opened for the current selection - a Rule
	// always watches a folder (see RuleCommands.java's equivalent check), but
	// selecting a single file is also allowed here since createRule() falls
	// back to that file's parent folder. "Create Rule" buttons across the
	// tool templates bind their `disabled` to this. Assigned in the
	// constructor body (not a field initializer) so it's set from the
	// constructor's own `mainFacade` parameter rather than depending on class
	// field initialization order.
	public canCreateRule$: Observable<boolean>;

	constructor(protected processName: string,
							protected submissionState: SubmissionStateService,
						  protected mainFacade: MainFacade) {
		try {
			this.formStateService = inject(ProcessFormStateService);
		} catch {
			this.formStateService = new ProcessFormStateService();
		}
		try {
			this.dialogService = inject(DialogService);
		} catch {
			this.dialogService = undefined;
		}
		try {
		  this.destroyRef = inject(DestroyRef);
		} catch {
		  this.destroyRef = undefined;
		}
		// `?? of(null)` covers unit tests that hand-construct a tool component
		// with a partial MainFacade mock lacking getSelectedNode$ (real usage
		// always gets a live Observable back).
		this.canCreateRule$ = (mainFacade.getSelectedNode$() ?? of(null)).pipe(
			map((node) => !!node?.data && (node.data.type === 'Folder' || !!node.parent?.data))
		);
	}

	get isSubmitting(): boolean {
	  return this.submissionState.getSubmitting(this.processName);
	}
	set isSubmitting(val: boolean) {
	  this.submissionState.setSubmitting(this.processName, val);
	}

	/**
	 * Restore the most recent form snapshot for this process and start tracking
	 * value changes back into the snapshot store. Call this from the subclass's
	 * `configForm()` right after `this.form = this.fb.group({...})`.
	 *
	 * The first call after app startup is a no-op (no snapshot yet) — defaults
	 * from the FormBuilder stand. Subsequent rebuilds re-hydrate from whatever
	 * the user last edited.
	 */
	protected persistForm(): void {
		if (!this.form) return;
		const saved = this.formStateService.get(this.processName) as
			| Record<string, unknown>
			| undefined;
		if (saved) {
			// patchValue (not setValue) tolerates form schemas adding new fields
			// between snapshots — e.g. an upgrade that adds a new param keeps
			// the new field at its default while restoring the rest.
			this.form.patchValue(saved, { emitEvent: false });
		}
		
		if (this.destroyRef) {
		  this.form.valueChanges
		    .pipe(takeUntilDestroyed(this.destroyRef))
		    .subscribe((value) => {
		      this.formStateService.set(this.processName, value);
		    });
		} else {
		  this.form.valueChanges.subscribe((value) => {
		    this.formStateService.set(this.processName, value);
		  });
		}
	}

	/**
	 * Submit the current form's request. When a folder (rather than a single
	 * file) is selected, confirms with the user first via a dialog showing
	 * how many images will actually run and letting them narrow that to one
	 * file type - see DirectorySubmitDialogComponent / jit-ui#131 - before
	 * submitting. Cancelling the dialog submits nothing.
	 */
	submit(request: Request) {

		this.isSubmitting = true;

		this.mainFacade.isSelectedFileFolder$().pipe(take(1)).subscribe((isFolder) => {
			if (isFolder) {
				this.confirmDirectorySubmit(request);
			} else {
				this.dispatch(request);
			}
		});
	}

	private confirmDirectorySubmit(request: Request): void {

		const folder: RawFileInfo | undefined = this.mainFacade.getSelectedFileInfo()?.rawData;
		if (!folder) {
			this.isSubmitting = false;
			return;
		}
		if (!this.dialogService) {
			this.dispatch(request, folder);
			return;
		}
		const ref = this.dialogService.open(DirectorySubmitDialogComponent, {
			header: 'Confirm Directory Submission',
			width: '30rem',
			data: { folder },
		});

		ref.onClose.pipe(take(1)).subscribe((result: DirectorySubmitDialogResult | null | undefined) => {
			if (!result) {
				this.isSubmitting = false;
				return;
			}
			const fileOverride: RawFileInfo = result.regex
				? { ...folder, relPath: `${folder.relPath.replace(/\/+$/, '')}/${result.regex}` }
				: folder;
			this.dispatch(request, fileOverride);
		});
	}

	/**
	 * Open the Create Rule dialog for the current tool, using `request` (built
	 * exactly as the caller would have built it for `submit()`) as the
	 * template applied to every file the Rule later discovers - see
	 * CreateRuleDialogComponent / doc/JIT-Rules.md Frontend Design.
	 *
	 * A Rule watches a folder, not a single file (jit-parent's RuleCommands
	 * rejects a single-file selection the same way), so a file selection
	 * falls back to that file's parent folder rather than being a no-op -
	 * templates should bind their button's `disabled` to `canCreateRule$`,
	 * which agrees with the fallback used here.
	 */
	createRule(request: Request): void {
		this.mainFacade.getSelectedNode$().pipe(take(1)).subscribe((node) => {
			if (!node?.data || !this.dialogService) return;

			const isFolder = node.data.type === 'Folder';
			const folder: RawFileInfo | undefined = isFolder
				? node.data.rawData
				: node.parent?.data?.rawData;
			if (!folder) return;

			this.dialogService!.open(CreateRuleDialogComponent, {
				header: 'Create Rule',
				width: '32rem',
				data: { folder, request },
			});
		});
	}

	private dispatch(request: Request, fileOverride?: RawFileInfo): void {
		const processResult = this.mainFacade.runProcess(request, fileOverride);
		 if (processResult && processResult.subscribe) {
		   processResult.subscribe({
		     next: () => { this.isSubmitting = true; },
		     error: () => { this.isSubmitting = false; },
		     complete: () => { this.isSubmitting = false; }
		   });
		 } else {
		   setTimeout(() => { this.isSubmitting = false; }, 2000);
		 }
	}
}
